import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useCms } from "../CmsContext";
import { PageHeader } from "../EditorChrome";
import { getCollection, timeAgo } from "../cmsUtils";

// newsroom is sorted by date automatically; legal is a fixed pair
const REORDERABLE = new Set(["team", "portfolio", "caseStudies", "caseCards"]);

export function CollectionPage() {
  const { collectionKey } = useParams();
  const collection = getCollection(collectionKey);
  const { api, API_BASE, refreshPublishStatus } = useCms();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    const res = await api.get(`${API_BASE}/entries/${collectionKey}`);
    if (res && res.success) {
      setEntries(res.entries || []);
    } else {
      setLoadError(res?.error || res?.message || "Could not reach the server");
    }
    setLoading(false);
  }, [api, API_BASE, collectionKey]);

  useEffect(() => {
    setQuery("");
    load();
  }, [load]);

  const filtered = useMemo(
    () =>
      entries.filter(
        (e) =>
          !query ||
          (e.label || e.slug).toLowerCase().includes(query.toLowerCase()) ||
          e.slug.toLowerCase().includes(query.toLowerCase())
      ),
    [entries, query]
  );

  const canReorder = REORDERABLE.has(collectionKey) && !query;

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= entries.length) return;
    const next = [...entries];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setEntries(next);
    const res = await api.post(`${API_BASE}/entries/reorder`, {
      collectionKey,
      slugs: next.map((e) => e.slug),
    });
    if (!res || !res.success) {
      toast.error("Reorder failed");
      load();
    } else {
      refreshPublishStatus();
    }
  };

  const remove = async (entry) => {
    if (
      !window.confirm(
        `Delete ${collection.singular.toLowerCase()} “${entry.label || entry.slug}”?\n\nIt will be removed from the site on the next publish. You can restore it later from Activity.`
      )
    ) {
      return;
    }
    const res = await api.post(`${API_BASE}/entries/delete`, {
      collectionKey,
      slug: entry.slug,
    });
    if (res && res.success) {
      toast.success("Deleted — will be removed on next publish");
      load();
      refreshPublishStatus();
    } else {
      toast.error(res?.error || "Delete failed");
    }
  };

  if (!collection) return <p className="lw-empty">Unknown collection.</p>;

  return (
    <div className="lw-page">
      <PageHeader
        title={collection.label}
        subtitle={collection.description}
        actions={
          <>
            <input
              className="lw-input lw-input--search"
              type="search"
              placeholder={`Search ${collection.label.toLowerCase()}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {collectionKey !== "legal" && (
              <Link to="new" className="lw-btn lw-btn--primary">
                + New {collection.singular.toLowerCase()}
              </Link>
            )}
          </>
        }
      />

      {loading ? (
        <p className="lw-empty">Loading…</p>
      ) : loadError ? (
        <div className="lw-empty">
          <p>
            Failed to load: <strong>{loadError}</strong>. Is the backend running?
          </p>
          <button
            type="button"
            className="lw-btn lw-btn--secondary lw-btn--sm"
            style={{ marginTop: 10 }}
            onClick={load}
          >
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <p className="lw-empty">
          {query ? "Nothing matches your search." : "No entries yet — create the first one."}
        </p>
      ) : (
        <ul className="lw-rowlist">
          {filtered.map((entry, i) => (
            <li key={entry.slug} className="lw-rowlist__li">
              <Link to={encodeURIComponent(entry.slug)} className="lw-row">
                <div className="lw-row__main">
                  <span className="lw-row__title">{entry.label || entry.slug}</span>
                  <span className="lw-row__sub">{entry.slug}</span>
                </div>
                <div className="lw-row__side">
                  {entry.status === "draft" ? (
                    <span className="lw-badge lw-badge--new">Not published yet</span>
                  ) : entry.hasUnpublishedChanges ? (
                    <span className="lw-badge lw-badge--draft">Unpublished changes</span>
                  ) : (
                    <span className="lw-badge lw-badge--live">Live</span>
                  )}
                  {entry.updatedTime && (
                    <span className="lw-row__meta">
                      {entry.updatedBy?.fullName ? `${entry.updatedBy.fullName} · ` : ""}
                      {timeAgo(entry.updatedTime)}
                    </span>
                  )}
                </div>
              </Link>
              <div className="lw-row__tools">
                {canReorder && (
                  <>
                    <button
                      type="button"
                      className="lw-iconbtn"
                      title="Move up"
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="lw-iconbtn"
                      title="Move down"
                      disabled={i === filtered.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      ↓
                    </button>
                  </>
                )}
                {collectionKey !== "legal" && (
                  <button
                    type="button"
                    className="lw-iconbtn lw-iconbtn--danger"
                    title="Delete"
                    onClick={() => remove(entry)}
                  >
                    ×
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
